import { AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { DocumentSelectionModal } from '@/components/tasks/task-detail/DocumentSelectionModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useGetBlobsQuery } from '@/services/api/blobService';
import type {
  BlobSummary,
  CaseExpenseStatus,
  CaseExpenseType,
  CreateCaseExpense,
  PayeeType,
  PaymentMethod,
  ReadCaseExpense,
  SimpleContactInfo,
} from '@/services/api/generated/caseExpensesService';
import {
  useCaseExpenseCreateMutation,
  useCaseExpenseLinkBlobMutation,
  useCaseExpenseUnlinkBlobMutation,
  useCaseExpenseUpdateMutation,
} from '@/services/api/generated/caseExpensesService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';

import { getPayeeDisplayName, inferPayeeType } from './caseExpenseUtils';
import { EXPENSE_TYPE_CONFIG, STATUS_CONFIG } from './constants';
import { ContactInfoForm } from './ContactInfoForm';

interface ContactInfoFormData {
  organization_name: string;
  given_name: string;
  family_name: string;
  phone_numbers: Array<{ label: string; value: string }>;
  email_addresses: Array<{ label: string; value: string }>;
  postal_addresses: Array<{
    label: string;
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  }>;
  note: string;
}

interface CaseExpenseFormData {
  expense_type: CaseExpenseType;
  payee_type: PayeeType;
  payee_id: string;
  contact_info: ContactInfoFormData;
  amount: string;
  payment_date: string;
  payment_method: PaymentMethod | '';
  payment_method_identifier: string;
  reason: string;
  status: CaseExpenseStatus;
}

const defaultContactInfo: ContactInfoFormData = {
  organization_name: '',
  given_name: '',
  family_name: '',
  phone_numbers: [{ label: 'main', value: '' }],
  email_addresses: [],
  postal_addresses: [],
  note: '',
};

interface CaseExpenseFormDialogProps {
  matterId: string;
  expense?: ReadCaseExpense;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for creating or editing a case expense.
 * Supports dynamic payee selection based on type.
 */
export const CaseExpenseFormDialog = ({
  matterId,
  expense,
  open,
  onOpenChange,
}: CaseExpenseFormDialogProps) => {
  const isEditMode = !!expense;

  // Fetch providers (for MEDICAL expense type)
  const { data: providers = [], isLoading: isLoadingProviders } =
    useGetMedicalProvidersQuery(matterId, { skip: !open });

  // Fetch police departments via police reports (for POLICE expense type)
  const { data: policeReports = [], isLoading: isLoadingPoliceReports } =
    usePoliceReportsListQuery({ matterId }, { skip: !open });

  // API mutations
  const [createExpense, { isLoading: isCreating }] =
    useCaseExpenseCreateMutation();
  const [updateExpense, { isLoading: isUpdating }] =
    useCaseExpenseUpdateMutation();
  const [linkBlob] = useCaseExpenseLinkBlobMutation();
  const [unlinkBlob] = useCaseExpenseUnlinkBlobMutation();

  const isSubmitting = isCreating || isUpdating;

  // Attachment management state
  const [documentSelectionOpen, setDocumentSelectionOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<BlobSummary | null>(null);
  // Pending attachment IDs for create mode (before expense exists)
  const [pendingBlobIds, setPendingBlobIds] = useState<string[]>([]);
  // Track newly linked/unlinked blobs in edit mode (for immediate UI update)
  const [editModeLinkedBlobIds, setEditModeLinkedBlobIds] = useState<string[]>(
    [],
  );
  const [editModeUnlinkedBlobIds, setEditModeUnlinkedBlobIds] = useState<
    string[]
  >([]);

  // Fetch blobs for displaying attachment details (names and download URLs)
  const { data: allBlobs = [], refetch: refetchBlobs } = useGetBlobsQuery(
    matterId,
    {
      skip: !open,
    },
  );

  // Get display attachments with full details (name, download_url)
  const displayAttachments = useMemo(() => {
    let blobIds: string[];

    if (isEditMode) {
      // In edit mode: start with expense blobs, add newly linked, remove unlinked
      const existingIds = expense?.blobs?.map((b) => b.id as string) ?? [];
      const combined = new Set([...existingIds, ...editModeLinkedBlobIds]);
      editModeUnlinkedBlobIds.forEach((id) => combined.delete(id));
      blobIds = Array.from(combined);
    } else {
      blobIds = pendingBlobIds;
    }

    return blobIds
      .map((id) => {
        const blob = allBlobs.find((b) => b.id === id);
        return blob
          ? { id: blob.id, name: blob.name, download_url: blob.download_url }
          : null;
      })
      .filter(
        (b): b is { id: string; name: string; download_url: string } =>
          b !== null,
      );
  }, [
    isEditMode,
    expense?.blobs,
    pendingBlobIds,
    editModeLinkedBlobIds,
    editModeUnlinkedBlobIds,
    allBlobs,
  ]);

  // Upload manager for document selection modal
  const { handleUploadToCollection } = useUploadManagerUnified({
    matterId,
    refetch: () => {
      // Refetch blobs to pick up newly uploaded files
      refetchBlobs();
    },
  });

  // Extract unique police departments from police reports
  const policeDepartments = useMemo(() => {
    const deptMap = new Map<string, { id: string; name: string }>();
    policeReports.forEach((report) => {
      if (report.police_department) {
        const dept = report.police_department;
        if (dept.id && dept.name) {
          deptMap.set(dept.id as string, {
            id: dept.id as string,
            name: dept.name,
          });
        }
      }
    });
    return Array.from(deptMap.values());
  }, [policeReports]);

  // Get default payee ID for the form
  const getDefaultPayeeId = useCallback((): string => {
    if (!expense) return '';
    const { payee } = expense;
    if (!payee) return '';

    switch (payee.type) {
      case 'PROVIDER':
        return payee.id as string;
      case 'COVERAGE':
        return payee.id as string;
      case 'POLICE_DEPARTMENT':
        return payee.id as string;
      case 'CONTACT':
        return ''; // Contact type uses inline contact_info, not a payee_id
      default:
        return '';
    }
  }, [expense]);

  // Get default contact info for the form (when editing a CONTACT payee)
  const getDefaultContactInfo = useCallback((): ContactInfoFormData => {
    if (!expense || expense.payee?.type !== 'CONTACT') {
      return defaultContactInfo;
    }
    const info = expense.payee.contact_info;
    const phones = info.phone_numbers ?? [];
    const emails = info.email_addresses ?? [];
    const addresses = info.postal_addresses ?? [];
    return {
      organization_name: info.organization_name ?? '',
      given_name: info.given_name ?? '',
      family_name: info.family_name ?? '',
      phone_numbers:
        phones.length > 0 ? phones : [{ label: 'main', value: '' }],
      email_addresses: emails,
      postal_addresses: addresses.map((addr) => ({
        label: addr.label,
        street: addr.street ?? '',
        city: addr.city ?? '',
        state: addr.state ?? '',
        postal_code: addr.postal_code ?? '',
        country: addr.country ?? 'US',
      })),
      note: info.note ?? '',
    };
  }, [expense]);

  // Get default payee type for the form
  const getDefaultPayeeType = useCallback((): PayeeType => {
    if (!expense) {
      // In create mode, infer payee type from the default expense type ('POLICE')
      return inferPayeeType('POLICE');
    }
    return expense.payee?.type ?? 'PROVIDER';
  }, [expense]);

  // Helper to get today's date in YYYY-MM-DD format
  const getTodayDate = () => new Date().toISOString().split('T')[0];

  // Form setup
  const form = useForm<CaseExpenseFormData>({
    defaultValues: {
      expense_type: expense?.expense_type ?? 'POLICE',
      payee_type: getDefaultPayeeType(),
      payee_id: getDefaultPayeeId(),
      contact_info: getDefaultContactInfo(),
      amount: expense?.amount ?? '',
      payment_date: expense?.payment_date ?? getTodayDate(),
      payment_method: expense?.payment_method ?? '',
      payment_method_identifier: expense?.payment_method_identifier ?? '',
      reason: expense?.reason ?? '',
      status: expense?.status ?? 'SENT',
    },
  });

  const { watch, setValue, reset, control } = form;
  const expenseType = watch('expense_type');
  const status = watch('status');
  const paymentMethod = watch('payment_method');
  const payeeType = inferPayeeType(expenseType);
  const prevExpenseTypeRef = useRef<CaseExpenseType>(expenseType);

  // Reset form when dialog opens/closes or expense changes
  useEffect(() => {
    if (open) {
      const defaultExpenseType = expense?.expense_type ?? 'POLICE';
      prevExpenseTypeRef.current = defaultExpenseType;
      reset({
        expense_type: defaultExpenseType,
        payee_type: getDefaultPayeeType(),
        payee_id: getDefaultPayeeId(),
        contact_info: getDefaultContactInfo(),
        amount: expense?.amount ?? '',
        payment_date: expense?.payment_date ?? getTodayDate(),
        payment_method: expense?.payment_method ?? '',
        payment_method_identifier: expense?.payment_method_identifier ?? '',
        reason: expense?.reason ?? '',
        status: expense?.status ?? 'SENT',
      });
      // Reset attachment state when dialog opens
      setPendingBlobIds([]);
      setEditModeLinkedBlobIds([]);
      setEditModeUnlinkedBlobIds([]);
    }
  }, [
    open,
    expense,
    reset,
    getDefaultPayeeId,
    getDefaultPayeeType,
    getDefaultContactInfo,
  ]);

  // Clear payee_id when expense type changes (only in create mode)
  useEffect(() => {
    if (open && !isEditMode && expenseType !== prevExpenseTypeRef.current) {
      const newPayeeType = inferPayeeType(expenseType);
      // In create mode, clear payee_id when expense type changes
      setValue('payee_id', '');
      setValue('payee_type', newPayeeType);
      // Reset contact_info to defaults when switching to CONTACT
      if (newPayeeType === 'CONTACT') {
        setValue('contact_info', defaultContactInfo);
      }
    }
    prevExpenseTypeRef.current = expenseType;
  }, [expenseType, open, setValue, isEditMode]);

  // Build SimpleContactInfo from form data
  const buildContactInfo = (
    formContactInfo: ContactInfoFormData,
  ): SimpleContactInfo => {
    return {
      organization_name: formContactInfo.organization_name || undefined,
      given_name: formContactInfo.given_name || undefined,
      family_name: formContactInfo.family_name || undefined,
      phone_numbers: formContactInfo.phone_numbers.filter((p) => p.value),
      email_addresses: formContactInfo.email_addresses.filter((e) => e.value),
      postal_addresses: formContactInfo.postal_addresses
        .filter((a) => a.street || a.city)
        .map((a) => ({
          label: a.label,
          street: a.street || undefined,
          city: a.city || undefined,
          state: a.state || undefined,
          postal_code: a.postal_code || undefined,
          country: a.country || undefined,
        })),
      note: formContactInfo.note || undefined,
    };
  };

  // Handle form submission
  const onSubmit = async (data: CaseExpenseFormData) => {
    try {
      if (isEditMode && expense) {
        // Update existing expense
        await updateExpense({
          matterId,
          id: expense.id as string,
          patchedUpdateCaseExpense: {
            expense_type: data.expense_type,
            status: data.status,
            payment_method: data.payment_method || undefined,
            payment_method_identifier: data.payment_method_identifier || null,
            payment_date: data.payment_date || null,
            amount: data.amount || null,
            reason: data.reason || null,
          },
        }).unwrap();
        toast.success('Case expense updated');
      } else {
        // Create new expense
        const createData: CreateCaseExpense = {
          expense_type: data.expense_type,
          payee_type: data.payee_type,
          amount: data.amount,
          payment_date: data.payment_date || undefined,
          payment_method: data.payment_method || undefined,
          payment_method_identifier: data.payment_method_identifier || undefined,
          reason: data.reason || undefined,
          status: data.status,
        };

        // Set payee_id or contact_info based on payee_type
        if (data.payee_type === 'CONTACT') {
          createData.contact_info = buildContactInfo(data.contact_info);
        } else {
          createData.payee_id = data.payee_id;
        }

        const newExpense = await createExpense({
          matterId,
          createCaseExpense: createData,
        }).unwrap();

        // Link pending attachments if any
        if (pendingBlobIds.length > 0) {
          try {
            await Promise.all(
              pendingBlobIds.map((blobId) =>
                linkBlob({
                  matterId,
                  id: newExpense.id as string,
                  createCaseExpenseBlob: { blob_id: blobId },
                }).unwrap(),
              ),
            );
          } catch {
            // Expense was created but attachments failed
            toast.warning(
              'Expense created but some attachments failed to link',
            );
            onOpenChange(false);
            return;
          }
        }

        toast.success('Case expense added');
      }
      onOpenChange(false);
    } catch {
      toast.error('Failed to save. Please try again.');
    }
  };

  // Handle dialog close
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !isSubmitting) {
      onOpenChange(false);
    }
  };

  // Handle selecting documents to link as attachments
  const handleSelectDocuments = async (blobIds: string[]) => {
    if (isEditMode && expense) {
      // Edit mode: link immediately
      const currentBlobIds = new Set([
        ...(expense.blobs?.map((b) => b.id as string) ?? []),
        ...editModeLinkedBlobIds,
      ]);
      // Exclude any that were unlinked in this session
      editModeUnlinkedBlobIds.forEach((id) => currentBlobIds.delete(id));

      const newBlobIds = blobIds.filter((id) => !currentBlobIds.has(id));

      if (newBlobIds.length === 0) return;

      try {
        await Promise.all(
          newBlobIds.map((blobId) =>
            linkBlob({
              matterId,
              id: expense.id as string,
              createCaseExpenseBlob: { blob_id: blobId },
            }).unwrap(),
          ),
        );
        // Track newly linked blobs for immediate UI update
        setEditModeLinkedBlobIds((prev) => [...prev, ...newBlobIds]);
        // If any were previously unlinked, remove from unlinked list
        setEditModeUnlinkedBlobIds((prev) =>
          prev.filter((id) => !newBlobIds.includes(id)),
        );
        // Refetch blobs to get names/download URLs
        await refetchBlobs();
        toast.success(`${newBlobIds.length} attachment(s) linked`);
      } catch {
        toast.error('Failed to link attachment');
      }
    } else {
      // Create mode: store pending blob IDs and refetch to get names
      setPendingBlobIds(blobIds);
      await refetchBlobs();
    }
  };

  // Handle unlinking an attachment
  const handleDeleteAttachment = async () => {
    if (!attachmentToDelete) return;
    const blobId = attachmentToDelete.id as string;

    if (isEditMode && expense) {
      // Edit mode: unlink from server
      try {
        await unlinkBlob({
          matterId,
          id: expense.id as string,
          blobId,
          pk: expense.id as string,
        }).unwrap();
        // Track unlinked blob for immediate UI update
        setEditModeUnlinkedBlobIds((prev) => [...prev, blobId]);
        // Remove from linked list if it was added in this session
        setEditModeLinkedBlobIds((prev) => prev.filter((id) => id !== blobId));
        toast.success('Attachment removed');
      } catch {
        toast.error('Failed to remove attachment');
      }
    } else {
      // Create mode: remove from pending list
      setPendingBlobIds((prev) => prev.filter((id) => id !== blobId));
    }
    setAttachmentToDelete(null);
  };

  // Determine if any payee data is loading (CONTACT doesn't need loading)
  const isLoadingPayees =
    payeeType === 'CONTACT'
      ? false
      : isLoadingProviders || isLoadingPoliceReports;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-2xl">
        <div className="max-h-[85vh] overflow-y-auto">
          {/* Premium Header */}
          <DialogHeader className="sticky top-0 z-10 border-b border-zinc-100 bg-white px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 ring-1 ring-primary/10">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold tracking-tight text-zinc-900">
                  {isEditMode ? 'Edit Expense' : 'New Expense'}
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-zinc-500">
                  Track firm disbursements for case costs
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="divide-y divide-zinc-100"
            >
              {/* Section 1: Expense Details */}
              <section className="space-y-6 px-8 py-6">
                <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 text-xs font-semibold text-zinc-600">
                    1
                  </span>
                  Expense Details
                </h3>

                {/* Expense Type - Segmented Control */}
                <div className="space-y-2">
                  <FormLabel className="text-sm font-medium text-zinc-700">
                    Type
                  </FormLabel>
                  <div
                    className={cn(
                      'grid grid-cols-4 gap-1 rounded-lg bg-zinc-100 p-1',
                      isEditMode && 'pointer-events-none opacity-60',
                    )}
                  >
                    {(['POLICE', 'MEDICAL', 'MAIL', 'OTHER'] as const).map(
                      (type) => {
                        const config = EXPENSE_TYPE_CONFIG[type];
                        const Icon = config.icon;
                        return (
                          <button
                            key={type}
                            type="button"
                            disabled={isEditMode}
                            onClick={() => setValue('expense_type', type)}
                            className={cn(
                              'flex items-center justify-center gap-1.5 rounded-md px-3 py-2.5 text-sm font-medium transition-all',
                              expenseType === type
                                ? 'bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-200'
                                : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900',
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="hidden sm:inline">
                              {config.label}
                            </span>
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>

                {/* Amount - Premium Input */}
                <FormField
                  control={form.control}
                  name="amount"
                  rules={{
                    required: 'Amount is required',
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: 'Enter a valid amount (e.g., 25.00)',
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-zinc-700">
                        Amount
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                            <span className="text-xl font-medium text-zinc-400">
                              $
                            </span>
                          </div>
                          <Input
                            {...field}
                            type="text"
                            placeholder="0.00"
                            disabled={isEditMode}
                            className="h-14 pl-9 text-2xl font-semibold text-zinc-900 placeholder:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-60"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Status - Pill Selection */}
                <div className="space-y-2">
                  <FormLabel className="text-sm font-medium text-zinc-700">
                    Status
                  </FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {(['UNPAID', 'SENT', 'PAID', 'CANCELLED'] as const).map(
                      (s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setValue('status', s)}
                          className={cn(
                            'rounded-full px-4 py-2 text-sm font-medium transition-all',
                            status === s
                              ? STATUS_CONFIG[s].formClass
                              : 'bg-zinc-50 text-zinc-500 hover:bg-zinc-100',
                          )}
                        >
                          {STATUS_CONFIG[s].label}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </section>

              {/* Section 2: Payee Information */}
              <section className="space-y-6 px-8 py-6">
                <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 text-xs font-semibold text-zinc-600">
                    2
                  </span>
                  Payee
                </h3>

                {/* Provider Selector - when expense type is MEDICAL */}
                {!isEditMode && payeeType === 'PROVIDER' && (
                  <FormField
                    control={form.control}
                    name="payee_id"
                    rules={{ required: 'Provider is required' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-zinc-700">
                          Medical Provider
                        </FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isLoadingProviders}
                          >
                            <SelectTrigger className="h-11 bg-white">
                              <SelectValue
                                placeholder={
                                  isLoadingProviders
                                    ? 'Loading providers...'
                                    : 'Select a provider'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {providers.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                  No providers available
                                </div>
                              ) : (
                                providers.map((provider) => (
                                  <SelectItem
                                    key={provider.id}
                                    value={provider.id}
                                  >
                                    {provider.name || 'Unnamed Provider'}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Police Department Selector - when expense type is POLICE */}
                {!isEditMode && payeeType === 'POLICE_DEPARTMENT' && (
                  <FormField
                    control={form.control}
                    name="payee_id"
                    rules={{ required: 'Police department is required' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-zinc-700">
                          Police Department
                        </FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isLoadingPoliceReports}
                          >
                            <SelectTrigger className="h-11 bg-white">
                              <SelectValue
                                placeholder={
                                  isLoadingPoliceReports
                                    ? 'Loading departments...'
                                    : 'Select a department'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {policeDepartments.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                  No police departments available
                                </div>
                              ) : (
                                policeDepartments.map((dept) => (
                                  <SelectItem key={dept.id} value={dept.id}>
                                    {dept.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Inline Contact Form - when payee_type is CONTACT */}
                <AnimatePresence mode="wait">
                  {!isEditMode && payeeType === 'CONTACT' && <ContactInfoForm />}
                </AnimatePresence>

                {/* Display payee info in edit mode (read-only) */}
                {isEditMode && expense && (
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                      Current Payee
                    </p>
                    <p className="mt-1 text-base font-medium text-zinc-900">
                      {getPayeeDisplayName(expense)}
                    </p>
                    <p className="text-sm text-zinc-500">
                      {expense.payee?.type ?? 'Unknown'}
                    </p>
                  </div>
                )}
              </section>

              {/* Section 3: Payment Details */}
              <section className="space-y-6 px-8 py-6">
                <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 text-xs font-semibold text-zinc-600">
                    3
                  </span>
                  Payment Details
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Payment Method */}
                  <FormField
                    control={form.control}
                    name="payment_method"
                    rules={{ required: 'Payment method is required' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-zinc-700">
                          Payment Method <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="h-11 bg-white">
                              <SelectValue placeholder="Select method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CREDIT_CARD">
                                Credit Card
                              </SelectItem>
                              <SelectItem value="CHEQUE">Cheque</SelectItem>
                              <SelectItem value="FIRM_PAYS">
                                Firm Pays
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Payment Date */}
                  <FormField
                    control={form.control}
                    name="payment_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-zinc-700">
                          Payment Date
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="date"
                            disabled={status === 'UNPAID'}
                            className="h-11 bg-white disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Payment Method Identifier - show when method is selected */}
                <FormField
                  control={form.control}
                  name="payment_method_identifier"
                  rules={{
                    required:
                      paymentMethod && paymentMethod !== 'FIRM_PAYS'
                        ? paymentMethod === 'CREDIT_CARD'
                          ? 'Last 4 digits is required'
                          : 'Cheque number is required'
                        : false,
                  }}
                  render={({ field }) =>
                    paymentMethod && paymentMethod !== 'FIRM_PAYS' ? (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-zinc-700">
                          {paymentMethod === 'CREDIT_CARD'
                            ? 'Last 4 Digits of Card'
                            : 'Cheque Number'}{' '}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder={
                              paymentMethod === 'CREDIT_CARD'
                                ? 'e.g., 4242'
                                : 'e.g., 1001'
                            }
                            maxLength={paymentMethod === 'CREDIT_CARD' ? 4 : 50}
                            className="h-11 bg-white"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    ) : (
                      <input type="hidden" {...field} />
                    )
                  }
                />

                {/* Reason */}
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-zinc-700">
                        Reason / Notes
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Optional notes about this expense"
                          rows={2}
                          className="resize-none bg-white"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </section>

              {/* Section 4: Attachments */}
              <section className="space-y-4 px-8 py-6">
                <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 text-xs font-semibold text-zinc-600">
                    4
                  </span>
                  Attachments
                </h3>

                {/* Attachments List */}
                {displayAttachments.length === 0 ? (
                  <div className="py-4 text-center text-sm text-zinc-500">
                    No attachments yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {displayAttachments.map((blob) => (
                      <div
                        key={blob.id}
                        className="group flex items-center justify-between rounded-lg border border-zinc-200 p-3 transition-colors hover:border-zinc-300"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <FileText className="h-4 w-4 flex-shrink-0 text-zinc-400" />
                          <a
                            href={blob.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate text-sm font-medium text-zinc-900 hover:text-primary hover:underline"
                            title={blob.name}
                          >
                            {blob.name}
                          </a>
                        </div>

                        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-zinc-500 hover:text-zinc-700"
                            onClick={() =>
                              window.open(blob.download_url, '_blank')
                            }
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-zinc-500 hover:text-red-600"
                            onClick={() =>
                              setAttachmentToDelete({
                                id: blob.id,
                                name: blob.name,
                              })
                            }
                            title="Remove attachment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Attachment Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDocumentSelectionOpen(true)}
                  className="w-full justify-center gap-2 border-dashed border-zinc-300 text-zinc-600 hover:border-zinc-400 hover:text-zinc-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Attachment
                </Button>
              </section>

              {/* Sticky Footer */}
              <DialogFooter className="sticky bottom-0 flex gap-3 border-t border-zinc-100 bg-white px-8 py-5">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                  className="text-zinc-600"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || (!isEditMode && isLoadingPayees)}
                  className="min-w-[120px] bg-primary hover:bg-primary/90"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isEditMode ? (
                    'Save Changes'
                  ) : (
                    'Add Expense'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>

      {/* Document Selection Modal */}
      <DocumentSelectionModal
        open={documentSelectionOpen}
        onClose={() => setDocumentSelectionOpen(false)}
        matterId={matterId}
        onSelectDocuments={handleSelectDocuments}
        initialSelectedBlobIds={
          isEditMode
            ? (expense?.blobs?.map((b) => b.id as string) ?? [])
            : pendingBlobIds
        }
        onUpload={handleUploadToCollection}
      />

      {/* Delete Attachment Confirmation */}
      <AlertDialog
        open={!!attachmentToDelete}
        onOpenChange={() => setAttachmentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove &ldquo;
              {attachmentToDelete?.name}&rdquo; from this expense?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAttachment}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
